import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import Header from './Header';
import Footer from './Footer';
import ToastExample from './ToastExample';

// Case Studies Data with theme colors
const caseStudiesData = [
  {
    id: 'bsides-albuquerque',
    title: 'BSides Albuquerque',
    subtitle: 'Cybersecurity Conference Platform',
    category: 'Conference Website',
    year: '2025',
    status: 'Live',
    url: 'https://bsidesabq.org/',
    themeColor: '#281447',
    screenshot: '/img/portfolio/bsides/bsides-header.png',
    description: 'A modern, engaging conference website for BSides Albuquerque 2025 cybersecurity conference featuring interactive animations, speaker management, and registration systems.',
    challenge: 'Design and develop a compelling conference website that captures the essence of cybersecurity while providing seamless user experience for attendees, speakers, and organizers.',
    solution: 'Created a modern, responsive website with cybersecurity-themed animations, comprehensive event management features, and integrated registration system.',
    technologies: ['React', 'Styled Components', 'Framer Motion', 'Event Management', 'Registration System'],
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    deviceType: 'desktop',
    images: { hero: '🎯', gallery: ['🔐', '🌐', '📱', '⚡'] }
  },
  {
    id: 'rbsec',
    title: 'RBSEC',
    subtitle: 'Cybersecurity Solutions Platform',
    category: 'Web Development',
    year: '2024',
    status: 'Live',
    themeColor: '#1d253d',
    screenshot: '/img/portfolio/rbsec/rbsec.png',
    description: 'Comprehensive cybersecurity platform offering security assessments, training modules, and professional consultancy services with advanced 3D visualizations.',
    challenge: 'Build a professional cybersecurity platform that showcases complex security services while maintaining user-friendly navigation and incorporating advanced visual elements.',
    solution: 'Developed a sophisticated platform with 3D animations, comprehensive service showcases, and streamlined user workflows for security assessments and training.',
    technologies: ['React', '2D Effects', '3D Animations'],
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    deviceType: 'desktop',
    images: { hero: '🔒', gallery: ['🛡️', '📊', '🎓', '⚙️'] }
  },
  {
    id: 'nupost',
    title: 'Nupost',
    subtitle: 'Digital Poster Creation App',
    category: 'Mobile Development',
    year: '2024',
    status: 'Live',
    themeColor: '#fe5501',
    screenshot: '/img/portfolio/nupost/nupost-header.png',
    description: 'Intuitive mobile application for creating stunning digital posters with templates, custom designs, and social sharing capabilities.',
    challenge: 'Create a user-friendly mobile app that allows users to design professional posters without prior design experience while maintaining creative flexibility.',
    solution: 'Developed a React Native app with drag-and-drop interface, extensive template library, and powerful customization tools that make design accessible to everyone.',
    technologies: ['React Native', 'Redux', 'Firebase', 'Image Processing', 'Social APIs'],
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    deviceType: 'mobile',
    images: { hero: '🎨', gallery: ['📱', '🎭', '✨', '🚀'] }
  },
  {
    id: 'pragunmart',
    title: 'Pragunmart',
    subtitle: 'Premium Dry Fruit E-commerce Platform',
    category: 'E-commerce Development',
    year: '2022',
    status: 'Discontinued',
    themeColor: '#217f13',
    screenshot: '/img/portfolio/pragunmart/header.png',
    description: 'Full-stack e-commerce platform for premium dry fruits with advanced inventory management, seamless checkout, and customer analytics.',
    challenge: 'Build a scalable e-commerce platform that handles complex inventory management for perishable goods while providing exceptional user experience and robust payment processing.',
    solution: 'Developed a comprehensive MERN stack solution with real-time inventory tracking, automated quality checks, and integrated payment gateway with advanced analytics dashboard.',
    technologies: ['MongoDB', 'Express.js', 'React', 'Node.js', 'Payment Gateway', 'Analytics'],
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    deviceType: 'tablet',
    images: { hero: '🛒', gallery: ['🥜', '📦', '💳', '📊'] }
  }
];

// Animations
const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(60px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const drawLinePath = keyframes`
  0% { 
    stroke-dashoffset: 500; 
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  100% { 
    stroke-dashoffset: 0; 
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// Main Container
const AppContainer = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
  background: #ffffff;
  min-height: 100vh;
  color: #2d3748;
`;

// Navigation Header
const NavHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #2d3748;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: #1a202c;
    transform: translateX(-2px);
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  a {
    color: #2d3748;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9rem;
    transition: color 0.2s ease;
    
    &:hover {
      color: #1a202c;
    }
  }
`;

// Container
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

// Hero Section
const HeroSection = styled.section`
  padding: 10rem 0 4rem;
  background: ${props => props.gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: #ffffff;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  animation: ${slideUp} 1s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const HeroCTA = styled.div`
  display: inline-block;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

// Overview Section
const OverviewSection = styled.section`
  padding: 8rem 0 6rem;
  background: #f7fafc;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const OverviewContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  animation: ${slideUp} 0.8s ease-out;
`;

const ProjectTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a202c;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ProjectSubtitle = styled.p`
  font-size: 1.2rem;
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const IllustrationContainer = styled.div`
  margin: 4rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  position: relative;
`;

const LogisticsIllustration = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 800px;
  position: relative;
`;

// Generic icon component for different case studies
const IconElement = styled.div`
  width: 80px;
  height: 80px;
  border: 2px solid ${props => props.color || '#e2e8f0'};
  border-radius: 8px;
  position: relative;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${floatAnimation} 3s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  
  &::before {
    content: '';
    position: absolute;
    ${props => props.beforeStyles}
  }
  
  &::after {
    content: '';
    position: absolute;
    ${props => props.afterStyles}
  }
`;

// BSides Icons
const SecurityShield = styled(IconElement)`
  &::before {
    top: 15px;
    left: 15px;
    right: 15px;
    bottom: 15px;
    border: 2px solid ${props => props.color};
    border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  }
  
  &::after {
    top: 30px;
    left: 30px;
    right: 30px;
    bottom: 30px;
    border: 1px solid ${props => props.color};
    border-radius: 50%;
  }
`;

const Terminal = styled(IconElement)`
  &::before {
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 35px;
    border: 2px solid ${props => props.color};
    border-radius: 4px;
  }
  
  &::after {
    bottom: 20px;
    left: 25px;
    width: 30px;
    height: 10px;
    border: 1px solid ${props => props.color};
    border-radius: 0 0 4px 4px;
  }
`;

const Conference = styled(IconElement)`
  &::before {
    top: 20px;
    left: 20px;
    right: 20px;
    height: 25px;
    border: 2px solid ${props => props.color};
    border-radius: 8px 8px 0 0;
  }
  
  &::after {
    bottom: 20px;
    left: 30px;
    right: 30px;
    height: 15px;
    border: 1px solid ${props => props.color};
    border-radius: 0 0 4px 4px;
    background: repeating-linear-gradient(
      90deg,
      transparent 0,
      transparent 3px,
      ${props => props.color} 3px,
      ${props => props.color} 4px
    );
  }
`;

// RBSec Icons
const SecurityLock = styled(IconElement)`
  &::before {
    top: 25px;
    left: 25px;
    right: 25px;
    height: 20px;
    border: 2px solid ${props => props.color};
    border-radius: 8px 8px 0 0;
  }
  
  &::after {
    bottom: 20px;
    left: 20px;
    right: 20px;
    height: 25px;
    border: 2px solid ${props => props.color};
    border-radius: 4px;
  }
`;

const Server = styled(IconElement)`
  &::before {
    top: 20px;
    left: 20px;
    right: 20px;
    height: 15px;
    border: 2px solid ${props => props.color};
    border-radius: 4px;
  }
  
  &::after {
    bottom: 20px;
    left: 20px;
    right: 20px;
    height: 15px;
    border: 2px solid ${props => props.color};
    border-radius: 4px;
  }
`;

const Network = styled(IconElement)`
  &::before {
    top: 30px;
    left: 30px;
    right: 30px;
    bottom: 30px;
    border: 2px solid ${props => props.color};
    border-radius: 50%;
  }
  
  &::after {
    top: 38px;
    left: 38px;
    right: 38px;
    bottom: 38px;
    border: 1px solid ${props => props.color};
    border-radius: 50%;
  }
`;

// NuPost Icons
const Palette = styled(IconElement)`
  &::before {
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    border: 2px solid ${props => props.color};
    border-radius: 50% 0 50% 50%;
  }
  
  &::after {
    bottom: 35px;
    right: 25px;
    width: 15px;
    height: 15px;
    border: 2px solid ${props => props.color};
    border-radius: 0 50% 50% 50%;
  }
`;

const Brush = styled(IconElement)`
  &::before {
    top: 15px;
    left: 25px;
    right: 25px;
    height: 35px;
    border: 2px solid ${props => props.color};
    border-radius: 8px 8px 0 0;
  }
  
  &::after {
    bottom: 15px;
    left: 35px;
    right: 35px;
    height: 20px;
    border: 1px solid ${props => props.color};
    border-radius: 0 0 4px 4px;
  }
`;

const Canvas = styled(IconElement)`
  &::before {
    top: 15px;
    left: 15px;
    right: 15px;
    bottom: 15px;
    border: 2px solid ${props => props.color};
    border-radius: 4px;
  }
  
  &::after {
    top: 25px;
    left: 25px;
    right: 25px;
    bottom: 25px;
    border: 1px solid ${props => props.color};
    border-radius: 2px;
  }
`;

// PragunMart Icons
const Store = styled(IconElement)`
  &::before {
    top: 15px;
    left: 15px;
    right: 15px;
    height: 30px;
    border: 2px solid ${props => props.color};
    border-radius: 8px 8px 0 0;
  }
  
  &::after {
    bottom: 15px;
    left: 15px;
    right: 15px;
    height: 25px;
    border: 2px solid ${props => props.color};
    border-radius: 0 0 4px 4px;
  }
`;

const Cart = styled(IconElement)`
  &::before {
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 35px;
    border: 2px solid ${props => props.color};
    border-radius: 4px 4px 0 0;
  }
  
  &::after {
    bottom: 20px;
    left: 25px;
    width: 8px;
    height: 8px;
    border: 2px solid ${props => props.color};
    border-radius: 50%;
    box-shadow: 20px 0 0 0 ${props => props.color};
  }
`;

const Package = styled(IconElement)`
  &::before {
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    border: 2px solid ${props => props.color};
    border-radius: 4px;
  }
  
  &::after {
    top: 20px;
    left: 20px;
    right: 20px;
    height: 2px;
    background: ${props => props.color};
    box-shadow: 0 20px 0 0 ${props => props.color};
  }
`;

const ConnectionLine = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: repeating-linear-gradient(
    to right,
    #cbd5e0 0,
    #cbd5e0 5px,
    transparent 5px,
    transparent 15px
  );
  z-index: -1;
`;

// Challenge & Solution Section
const ChallengeSolutionSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const ChallengePanel = styled.div`
  background: #ffffff;
  padding: 6rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const SolutionPanel = styled.div`
  background: ${props => props.themeColor || '#2d3748'};
  color: #ffffff;
  padding: 6rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 2rem;
  }
`;

const PanelTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PanelText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  opacity: 0.9;
`;

// Enhanced SVG Animation Container
const AnimatedSVGContainer = styled.div`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  width: 200px;
  height: 100px;
  opacity: 0.3;
`;

const AnimatedSVG = styled.svg`
  width: 100%;
  height: 100%;
`;

const AnimatedPath = styled.path`
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-dasharray: 500;
  stroke-dashoffset: 500;
  animation: ${drawLinePath} 4s ease-in-out forwards;
  animation-delay: 0.5s;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

// Wireframing Section
const WireframingSection = styled.section`
  padding: 8rem 0;
  background: #edf2f7;
  position: relative;
  overflow: hidden;
`;

const WireframingContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 4rem;
    text-align: center;
  }
`;

const WireframingText = styled.div`
  animation: ${slideUp} 0.8s ease-out;
`;

const WireframingTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #1a202c;
  letter-spacing: -0.02em;
`;

const WireframingDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #4a5568;
  margin-bottom: 2rem;
`;

const WireframingDetails = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: #4a5568;
`;

const WireframeContainer = styled.div`
  position: relative;
  perspective: 1000px;
  animation: ${slideUp} 0.8s ease-out 0.2s both;
`;

// Device-specific wireframe mockups
const DesktopMockup = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
  transform: rotateY(-5deg) rotateX(5deg);
  transition: transform 0.3s ease;
  width: 100%;
  max-width: 450px;
  
  &:hover {
    transform: rotateY(0deg) rotateX(0deg);
  }
`;

const TabletMockup = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
  transform: rotateY(-8deg) rotateX(8deg);
  transition: transform 0.3s ease;
  width: 300px;
  height: 400px;
  
  &:hover {
    transform: rotateY(0deg) rotateX(0deg);
  }
`;

const MobileMockup = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  padding: 1rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.1);
  transform: rotateY(-10deg) rotateX(10deg);
  transition: transform 0.3s ease;
  width: 200px;
  height: 350px;
  
  &:hover {
    transform: rotateY(0deg) rotateX(0deg);
  }
`;

const WireframeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: ${props => props.mobile ? '1rem' : '2rem'};
  padding-bottom: ${props => props.mobile ? '0.5rem' : '1rem'};
  border-bottom: 1px solid #e2e8f0;
`;

const WireframeDot = styled.div`
  width: ${props => props.mobile ? '6px' : '8px'};
  height: ${props => props.mobile ? '6px' : '8px'};
  border-radius: 50%;
  background: ${props => props.color || '#e2e8f0'};
`;

const WireframeTitle = styled.div`
  margin-left: auto;
  font-size: ${props => props.mobile ? '0.7rem' : '0.9rem'};
  font-weight: 600;
  color: ${props => props.themeColor || '#4299e1'};
`;

const WireframeLayout = styled.div`
  display: ${props => props.mobile ? 'flex' : 'grid'};
  flex-direction: ${props => props.mobile ? 'column' : 'row'};
  grid-template-columns: ${props => props.tablet ? '1fr 1fr' : '1fr 2fr 1fr'};
  gap: ${props => props.mobile ? '0.5rem' : '1rem'};
  margin-bottom: ${props => props.mobile ? '1rem' : '2rem'};
`;

const WireframeBlock = styled.div`
  height: ${props => {
    if (props.mobile) return props.height ? `${parseInt(props.height) * 0.6}px` : '36px';
    if (props.tablet) return props.height ? `${parseInt(props.height) * 0.8}px` : '48px';
    return props.height || '60px';
  }};
  background: ${props => props.primary ? (props.themeColor || '#4299e1') : '#f7fafc'};
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.primary ? '#ffffff' : '#a0aec0'};
  font-size: ${props => props.mobile ? '0.6rem' : '0.8rem'};
  font-weight: 500;
`;

const WireframeContent = styled.div`
  display: ${props => props.mobile ? 'flex' : 'grid'};
  flex-direction: ${props => props.mobile ? 'column' : 'row'};
  grid-template-columns: ${props => props.tablet ? '1fr' : '1fr 1fr'};
  gap: ${props => props.mobile ? '0.5rem' : '1rem'};
  margin-bottom: ${props => props.mobile ? '1rem' : '2rem'};
`;

const WireframeButtons = styled.div`
  display: flex;
  flex-direction: ${props => props.mobile ? 'column' : 'row'};
  gap: ${props => props.mobile ? '0.5rem' : '1rem'};
  justify-content: center;
`;

const WireframeButton = styled.div`
  padding: ${props => props.mobile ? '0.4rem 1rem' : '0.5rem 1.5rem'};
  background: ${props => props.primary ? (props.themeColor || '#4299e1') : 'transparent'};
  color: ${props => props.primary ? '#ffffff' : (props.themeColor || '#4299e1')};
  border: 1px solid ${props => props.themeColor || '#4299e1'};
  border-radius: 6px;
  font-size: ${props => props.mobile ? '0.6rem' : '0.8rem'};
  font-weight: 500;
  text-align: center;
`;

// Landing Page Section
const LandingPageSection = styled.section`
  background: ${props => props.gradient || 'linear-gradient(135deg, #9e5871ff, #d53f8c)'};
  color: #ffffff;
  padding: 8rem 0;
  position: relative;
  overflow: hidden;
`;

const LandingPageContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6rem;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 4rem;
    text-align: center;
  }
`;

const LandingPageText = styled.div`
  animation: ${slideUp} 0.8s ease-out;
`;

const LandingPageTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
`;

const LandingPageDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const LandingPageDetails = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  opacity: 0.8;
`;

const DeviceMockupContainer = styled.div`
  position: relative;
  animation: ${slideUp} 0.8s ease-out 0.2s both;
`;

const DeviceMockup = styled.div`
  background: #1a202c;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
`;

const MockupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: #2d3748;
  border-radius: 4px;
`;

const MockupContent = styled.div`
  background: #4a5568;
  border-radius: 4px;
  padding: 1rem;
  color: #ffffff;
  text-align: center;
`;

const MockupImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 1rem;
`;

const MockupTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const MockupText = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 1.5rem;
`;

const MockupButton = styled.div`
  background: #e53e3e;
  color: #ffffff;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-weight: 600;
  display: inline-block;
`;

// Related Case Studies Section
const RelatedSection = styled.section`
  padding: 8rem 0;
  background: #f7fafc;
  position: relative;
`;

const RelatedTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  position: relative;
`;

const RelatedCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  animation: ${slideUp} 0.6s ease-out;
  animation-delay: ${props => props.delay * 0.1}s;
  animation-fill-mode: both;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const RelatedImage = styled.div`
  height: 160px;
  background: ${props => props.gradient || '#edf2f7'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #4a5568;
`;

const RelatedCardContent = styled.div`
  padding: 1.5rem;
`;

const RelatedCategory = styled.div`
  font-size: 0.8rem;
  color: #4299e1;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const RelatedCardTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1a202c;
`;

const RelatedCardSubtitle = styled.p`
  font-size: 0.9rem;
  color: #4a5568;
  line-height: 1.5;
`;

const ConnectorSVG = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
`;

const ConnectorLine = styled.path`
  fill: none;
  stroke: #cbd5e0;
  stroke-width: 2;
  stroke-dasharray: 500;
  stroke-dashoffset: 500;
  opacity: 0.6;
  animation: ${drawLinePath} 3s ease-in-out forwards;
  animation-delay: 1s;
`;

// Scroll Position Hook
const useScrollAnimation = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};



// Helper function to get case study icons
const getCaseStudyIcons = (caseStudy) => {
  switch(caseStudy?.id) {
    case 'bsides-albuquerque':
      return (
        <>
          <SecurityShield color={caseStudy.themeColor} delay="0s" />
          <Terminal color={caseStudy.themeColor} delay="0.3s" />
          <Conference color={caseStudy.themeColor} delay="0.6s" />
        </>
      );
    case 'rbsec':
      return (
        <>
          <SecurityLock color={caseStudy.themeColor} delay="0s" />
          <Server color={caseStudy.themeColor} delay="0.3s" />
          <Network color={caseStudy.themeColor} delay="0.6s" />
        </>
      );
    case 'nupost':
      return (
        <>
          <Palette color={caseStudy.themeColor} delay="0s" />
          <Brush color={caseStudy.themeColor} delay="0.3s" />
          <Canvas color={caseStudy.themeColor} delay="0.6s" />
        </>
      );
    case 'pragunmart':
      return (
        <>
          <Store color={caseStudy.themeColor} delay="0s" />
          <Cart color={caseStudy.themeColor} delay="0.3s" />
          <Package color={caseStudy.themeColor} delay="0.6s" />
        </>
      );
    default:
      return (
        <>
          <SecurityShield color="#4299e1" delay="0s" />
          <Terminal color="#4299e1" delay="0.3s" />
          <Conference color="#4299e1" delay="0.6s" />
        </>
      );
  }
};

// Helper function to get case study image
const getCaseStudyImage = (caseStudy) => {
  const imageMap = {
    'bsides-albuquerque': '/img/portfolio/bsides-header.png',
    'rbsec': '/img/portfolio/rbsec.png',
    'nupost': '/img/portfolio/nupost-header.png',
    'pragunmart': '/img/portfolio/header.png'
  };
  
  return imageMap[caseStudy?.id] || '/img/portfolio/default.png';
};

// Helper function to get case study content
const getCaseStudyContent = (caseStudy) => {
  const contentMap = {
    'bsides-albuquerque': {
      title: 'BSides Albuquerque 2025',
      text: 'Join the most innovative cybersecurity conference where security enthusiasts and experts come together to share knowledge, exchange ideas, and strengthen our collective security awareness.',
      button: 'REGISTER NOW'
    },
    'rbsec': {
      title: 'RBSec Security Solutions',
      text: 'Professional cybersecurity consulting services designed to protect your digital assets and secure your business infrastructure with expert-level security implementations.',
      button: 'CONTACT US'
    },
    'nupost': {
      title: 'NuPost Design Studio',
      text: 'Unleash your creativity with our powerful design tools. Create stunning posters, graphics, and visual content with professional-grade features and intuitive interface.',
      button: 'START DESIGNING'
    },
    'pragunmart': {
      title: 'PragunMart Marketplace',
      text: 'Complete e-commerce solution connecting vendors and customers with advanced inventory management, secure payments, and seamless shopping experience.',
      button: 'SHOP NOW'
    }
  };
  
  return contentMap[caseStudy?.id] || {
    title: 'Project Overview',
    text: 'Comprehensive digital solution designed to meet modern business requirements with cutting-edge technology and user-centered design approach.',
    button: 'LEARN MORE'
  };
};

// Helper function to render wireframe based on device type
const renderWireframe = (caseStudy) => {
  const isMobile = caseStudy.deviceType === 'mobile';
  const isTablet = caseStudy.deviceType === 'tablet';
  const isDesktop = caseStudy.deviceType === 'desktop';

  const MockupComponent = isMobile ? MobileMockup : isTablet ? TabletMockup : DesktopMockup;

  return (
    <MockupComponent>
      <WireframeHeader mobile={isMobile}>
        <WireframeDot color="#ff5f56" mobile={isMobile} />
        <WireframeDot color="#ffbd2e" mobile={isMobile} />
        <WireframeDot color="#27ca3f" mobile={isMobile} />
        <WireframeTitle themeColor={caseStudy.themeColor} mobile={isMobile}>
          {caseStudy.title}
        </WireframeTitle>
      </WireframeHeader>
      
      <WireframeLayout mobile={isMobile} tablet={isTablet}>
        <WireframeBlock 
          height={isMobile ? "24px" : isTablet ? "36px" : "30px"} 
          mobile={isMobile} 
          tablet={isTablet}
          themeColor={caseStudy.themeColor}
        >
          Menu
        </WireframeBlock>
        <WireframeBlock 
          height={isMobile ? "24px" : isTablet ? "36px" : "30px"} 
          primary 
          mobile={isMobile} 
          tablet={isTablet}
          themeColor={caseStudy.themeColor}
        >
          {isMobile ? "CTA" : "Primary CTA"}
        </WireframeBlock>
        {!isMobile && (
          <WireframeBlock 
            height={isTablet ? "36px" : "30px"} 
            tablet={isTablet}
            themeColor={caseStudy.themeColor}
          >
            Features
          </WireframeBlock>
        )}
      </WireframeLayout>
      
      <WireframeContent mobile={isMobile} tablet={isTablet}>
        <WireframeBlock 
          height={isMobile ? "72px" : isTablet ? "96px" : "120px"} 
          mobile={isMobile} 
          tablet={isTablet}
          themeColor={caseStudy.themeColor}
        >
          {isMobile ? "Main" : "Main Content"}
        </WireframeBlock>
        {!isMobile && (
          <div>
            <WireframeBlock 
              height={isTablet ? "32px" : "40px"} 
              style={{marginBottom: isMobile ? '0.5rem' : '1rem'}} 
              tablet={isTablet}
              themeColor={caseStudy.themeColor}
            >
              Info
            </WireframeBlock>
            <WireframeBlock 
              height={isTablet ? "48px" : "60px"} 
              tablet={isTablet}
              themeColor={caseStudy.themeColor}
            >
              Elements
            </WireframeBlock>
          </div>
        )}
      </WireframeContent>
      
      <WireframeButtons mobile={isMobile}>
        <WireframeButton 
          primary 
          mobile={isMobile}
          themeColor={caseStudy.themeColor}
        >
          {isMobile ? "Primary" : "Primary Action"}
        </WireframeButton>
        <WireframeButton 
          mobile={isMobile}
          themeColor={caseStudy.themeColor}
        >
          {isMobile ? "Secondary" : "Secondary Action"}
        </WireframeButton>
      </WireframeButtons>
    </MockupComponent>
  );
};

// Icons
const ArrowLeft = () => <span>←</span>;
const ArrowRight = () => <span>→</span>;
const ExternalLink = () => <span>↗</span>;

// Case Study Card Component
const CaseStudyCard = ({ caseStudy, onSelect }) => (
  <div onClick={() => onSelect(caseStudy)} style={{
    background: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(0,0,0,0.05)',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  }}>
    <div style={{
      height: '160px',
      background: caseStudy.gradient || '#edf2f7',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {caseStudy.screenshot ? (
        <img src={caseStudy.screenshot} alt={caseStudy.title} style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        }} />
      ) : (
        <div style={{fontSize: '2rem'}}>{caseStudy.images?.hero}</div>
      )}
    </div>
    
    <div style={{padding: '1.5rem'}}>
      <div style={{
        fontSize: '0.8rem',
        color: caseStudy.themeColor || '#4299e1',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '0.5rem'
      }}>
        {caseStudy.category}
      </div>
      <h3 style={{
        fontSize: '1.1rem',
        fontWeight: '700',
        marginBottom: '0.5rem',
        color: '#1a202c'
      }}>
        {caseStudy.title}
      </h3>
      <p style={{
        fontSize: '0.9rem',
        color: '#4a5568',
        lineHeight: '1.5'
      }}>
        {caseStudy.subtitle}
      </p>
    </div>
  </div>
);

// Enhanced Case Study Detail
const EnhancedCaseStudyDetail = ({ caseStudy, onBack }) => {
  const [overviewRef, overviewVisible] = useScrollAnimation();
  const [challengeRef, challengeVisible] = useScrollAnimation();
  const [wireframeRef, wireframeVisible] = useScrollAnimation();
  const [landingRef, landingVisible] = useScrollAnimation();
  const [showToast, setShowToast] = useState(true);

  if (!caseStudy) return null;

  const handleSelectCase = (caseStudy) => {
  console.log(`Clicked on case study: ${caseStudy.title}`);
  setShowToast(true); // Show toast instead of navigation
};

  // Get related case studies (same category, excluding current)
  const relatedCaseStudies = caseStudiesData
    .filter(cs => cs.category === caseStudy.category && cs.id !== caseStudy.id)
    .slice(0, 3);

  const mockupContent = getCaseStudyContent(caseStudy);

  return (
    <AppContainer>
      <NavHeader>
        <BackButton onClick={onBack}>
          <ArrowLeft /> Back to Home
        </BackButton>
        <NavLinks>
          <a href="/case-studies">Case Studies</a>
          <a href="/contact">Contact Us</a>
        </NavLinks>
      </NavHeader>

      {/* Hero Section */}
      <HeroSection gradient={caseStudy.gradient}>
        <Container>
          <HeroContent>
            <HeroTitle>{caseStudy.title}</HeroTitle>
            <HeroSubtitle>
              {caseStudy.description}
            </HeroSubtitle>
            <HeroCTA>
              Explore Project Details
            </HeroCTA>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* Overview Section */}
      <OverviewSection ref={overviewRef}>
        <Container>
          <OverviewContent>
            <ProjectTitle>Overview</ProjectTitle>
            <ProjectSubtitle>
              {caseStudy.description}
            </ProjectSubtitle>
            
            <IllustrationContainer>
              <LogisticsIllustration>
                {getCaseStudyIcons(caseStudy)}
                <ConnectionLine />
              </LogisticsIllustration>
            </IllustrationContainer>
          </OverviewContent>
        </Container>
      </OverviewSection>

      {/* Challenge & Solution Section */}
      <ChallengeSolutionSection ref={challengeRef}>
        <ChallengePanel>
          <PanelTitle>Challenges</PanelTitle>
          <PanelText>
            {caseStudy.challenge}
          </PanelText>
          
          <AnimatedSVGContainer>
            <AnimatedSVG viewBox="0 0 200 100" xmlns="http://www.w3.org/2000/svg">
              <AnimatedPath d="M10,80 Q30,20 60,50 Q90,80 120,40 Q150,10 190,30" />
              <AnimatedPath d="M20,70 Q50,30 80,60 Q110,90 140,50 Q170,20 190,40" style={{animationDelay: '1s'}} />
            </AnimatedSVG>
          </AnimatedSVGContainer>
        </ChallengePanel>
        
        <SolutionPanel themeColor={caseStudy.themeColor}>
          <PanelTitle>Solution</PanelTitle>
          <PanelText>
            {caseStudy.solution}
          </PanelText>
          
          <AnimatedSVGContainer>
            <AnimatedSVG viewBox="0 0 200 100" xmlns="http://www.w3.org/2000/svg">
              <AnimatedPath d="M10,50 Q40,10 70,30 Q100,50 130,20 Q160,40 190,20" />
              <AnimatedPath d="M10,60 Q40,20 70,40 Q100,60 130,30 Q160,50 190,30" style={{animationDelay: '1s'}} />
              <AnimatedPath d="M10,40 Q40,70 70,50 Q100,30 130,60 Q160,20 190,50" style={{animationDelay: '1.5s'}} />
            </AnimatedSVG>
          </AnimatedSVGContainer>
        </SolutionPanel>
      </ChallengeSolutionSection>

      {/* Wireframing Section */}
      <WireframingSection ref={wireframeRef}>
        <Container>
          <WireframingContent>
            <WireframingText>
              <WireframingTitle>Design Process</WireframingTitle>
              <WireframingDescription>
                Our comprehensive wireframing process involves multiple iterations to bring ideas to reality and validate concepts with stakeholders. We focus on user experience, information architecture, and intuitive navigation patterns.
              </WireframingDescription>
              <WireframingDetails>
                The wireframing phase includes user journey mapping, content strategy, interaction design, and usability testing. We create low-fidelity wireframes first, then progress to high-fidelity prototypes with detailed interactions and animations. This iterative approach ensures that the final design meets both user needs and business objectives while maintaining technical feasibility.
              </WireframingDetails>
            </WireframingText>
            
            <WireframeContainer>
              {renderWireframe(caseStudy)}
            </WireframeContainer>
          </WireframingContent>
        </Container>
      </WireframingSection>

      {/* Landing Page Section */}
      <LandingPageSection ref={landingRef} gradient={caseStudy.gradient}>
        <Container>
          <LandingPageContent>
            <LandingPageText>
              <LandingPageTitle>Final Implementation</LandingPageTitle>
              <LandingPageDescription>
                The landing page represents the culmination of our design and development process, featuring optimized user experience, responsive design, and seamless functionality across all devices and platforms.
              </LandingPageDescription>
              <LandingPageDetails>
                Our implementation focuses on performance optimization, accessibility compliance, and conversion optimization. We utilize modern web technologies, implement progressive enhancement principles, and ensure cross-browser compatibility. The final product delivers exceptional user experience while meeting all business requirements and technical specifications.
              </LandingPageDetails>
            </LandingPageText>
            
            <DeviceMockupContainer>
              <DeviceMockup>
                <MockupHeader>
                  <WireframeDot color="#ff5f56" />
                  <WireframeDot color="#ffbd2e" />
                  <WireframeDot color="#27ca3f" />
                </MockupHeader>
                
                <MockupContent>
                  <MockupImage 
                    src={getCaseStudyImage(caseStudy)}
                    alt={caseStudy.title}
                  />
                  <MockupTitle>{mockupContent.title}</MockupTitle>
                  <MockupText>
                    {mockupContent.text}
                  </MockupText>
                  <MockupButton>{mockupContent.button}</MockupButton>
                </MockupContent>
              </DeviceMockup>
            </DeviceMockupContainer>
          </LandingPageContent>
        </Container>
      </LandingPageSection>

      {/* Related Case Studies */}
      {relatedCaseStudies.length > 0 && (
        <RelatedSection>
          <Container>
            <RelatedTitle>Related Case Studies</RelatedTitle>
            <RelatedGrid>
              <ConnectorSVG>
                <ConnectorLine d="M100,50 Q300,100 500,50 Q700,0 900,50" />
              </ConnectorSVG>
              
              {relatedCaseStudies.map((related, index) => (
                <RelatedCard key={related.id} delay={index}>
                  <RelatedImage gradient={related.gradient}>
                    {related.screenshot ? (
                      <img 
                        src={related.screenshot} 
                        alt={related.title}
                        style={{width: '100%', height: '100%', objectFit: 'cover'}}
                      />
                    ) : (
                      related.images?.hero || '🚀'
                    )}
                  </RelatedImage>
                  <RelatedCardContent>
                    <RelatedCategory style={{color: related.themeColor}}>
                      {related.category}
                    </RelatedCategory>
                    <RelatedCardTitle>{related.title}</RelatedCardTitle>
                    <RelatedCardSubtitle>{related.subtitle}</RelatedCardSubtitle>
                  </RelatedCardContent>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </Container>
        </RelatedSection>
      )}
      {showToast && (
  <ToastExample
    isVisible={showToast}
    onClose={() => setShowToast(false)}
    autoClose={true}
    duration={8000}
    showActions={true}
  />
)}
    </AppContainer>
  );
};

// Main Component
const CaseStudies = () => {
  const [selectedCase, setSelectedCase] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      const caseStudy = caseStudiesData.find(c => c.id === id);
      if (caseStudy) {
        setSelectedCase(caseStudy);
      } else {
        navigate('/case-studies');
      }
    } else {
      setSelectedCase(null);
    }
  }, [id, navigate]);

  const handleSelectCase = (caseStudy) => {
    navigate(`/portfolio/${caseStudy.id}`);
  };

  const handleBackToPortfolio = () => {
    navigate('/case-studies');
  };

  // Show detail view if case is selected
  if (selectedCase) {
    return (
      <EnhancedCaseStudyDetail 
        caseStudy={selectedCase} 
        onBack={handleBackToPortfolio} 
      />
    );
  }

  // Show case studies grid
  return (
    <AppContainer>
      <Header />
      
      {/* Hero Section */}
      <div style={{
        padding: '6rem 0 3rem',
        textAlign: 'center',
        background: '#f7fafc'
      }}>
        <Container>
          <div style={{
            fontSize: '0.75rem',
            color: '#4a5568',
            fontWeight: '500',
            marginBottom: '1.5rem',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            UI/UX DELIVERY
          </div>
          
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '1rem',
            color: '#1a202c'
          }}>
            Strategic <span style={{color: '#4299e1'}}>Design Solutions</span>
          </h1>
          
          <p style={{
            fontSize: '1rem',
            color: '#4a5568',
            lineHeight: '1.5',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Explore detailed case studies showcasing design process, user research, 
            wireframing, and final implementation of digital products.
          </p>
        </Container>
      </div>

      {/* Case Studies Grid */}
      <div style={{
        padding: '4rem 0 6rem',
        background: '#ffffff'
      }}>
        <Container>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem'
          }}>
            {caseStudiesData.map((caseStudy) => (
              <CaseStudyCard 
                key={caseStudy.id} 
                caseStudy={caseStudy} 
                onSelect={handleSelectCase}
              />
            ))}
          </div>
        </Container>
      </div>

      <Footer />
    </AppContainer>
  );
};

export default CaseStudies;